// @ts-check
/// <reference path="../types/index.js" />

import React, { createContext, useState, useCallback } from 'react';

/** @type {SaveWindowDataContextType} */
const defaultContextValue = {
  triggerAllSaves: () => {},
  triggerSaveByLabel: () => {},
  registerSaveFunction: () => {},
  unregisterSaveFunction: () => {},
};

const SaveWindowDataContext = createContext(defaultContextValue);

/**
 * Provides the SaveWindowDataContext to child components.
 * @param {{ children: React.ReactNode }} props - The props containing children to be rendered within the provider.
 * @return {JSX.Element} The provider component.
 */
export const SaveWindowDataProvider = ({ children }) => {
  const [saveFunctions, setSaveFunctions] = useState({});

  const registerSaveFunction = useCallback((label, fn) => {
    setSaveFunctions((prev) => ({ ...prev, [label]: fn }));
  }, []);

  const unregisterSaveFunction = useCallback((label) => {
    setSaveFunctions((prev) => {
      const newState = { ...prev };
      delete newState[label];
      return newState;
    });
  }, []);

  const triggerAllSaves = useCallback(() => {
    Object.values(saveFunctions).forEach((fn) => fn());
  }, [saveFunctions]);

  const triggerSaveByLabel = useCallback(
    (label) => {
      if (saveFunctions[label]) {
        saveFunctions[label]();
      }
    },
    [saveFunctions],
  );

  return (
    <SaveWindowDataContext.Provider
      value={{
        triggerAllSaves,
        triggerSaveByLabel,
        registerSaveFunction,
        unregisterSaveFunction,
      }}
    >
      {children}
    </SaveWindowDataContext.Provider>
  );
};

export default SaveWindowDataContext;
